/**
 * Intraday Book Exposure: Horizon / Meridian toggle (CIO request 2026-09-25).
 *
 * Pure state + label logic for the Signals page's Intraday Book Exposure section, kept out of the
 * page so it can be unit-tested. The rest of the dashboard stays Horizon-only until the full fund
 * switch (M7); this toggle scopes ONLY that section.
 *
 * Rules (CIO decisions, 2026-09-25):
 *  - Horizon and Meridian are never blended. Every figure shown belongs to the selected fund; a
 *    payload labelled for another fund is refused (payloadBelongsTo).
 *  - Day P&L: IBKR's own per-account P&L when IBKR splits it by account (source "ibkr_pnl");
 *    otherwise the backend's estimate from NAV change since the last close ("nav_change_estimate"),
 *    always labelled as such; an unfunded fund ("not_funded") shows its live IBKR value and
 *    "Not funded", never an estimate.
 *  - The toggle choice is remembered per browser (localStorage, every access try/catch-wrapped:
 *    private windows and blocked storage must never break the page).
 */

export type IntradayFundCode = "HORIZON" | "MERIDIAN";

export const INTRADAY_FUNDS: { code: IntradayFundCode; label: string }[] = [
  { code: "HORIZON", label: "Horizon" },
  { code: "MERIDIAN", label: "Meridian" },
];

export const DEFAULT_INTRADAY_FUND: IntradayFundCode = "HORIZON";
export const INTRADAY_FUND_STORAGE_KEY = "helion.signals.intradayBookFund";

type StorageLike = { getItem(k: string): string | null; setItem(k: string, v: string): void };

function defaultStorage(): StorageLike | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

export function normalizeIntradayFund(v: unknown): IntradayFundCode | null {
  const s = String(v ?? "").trim().toUpperCase();
  return s === "HORIZON" || s === "MERIDIAN" ? s : null;
}

/** The remembered choice for this browser; Horizon when nothing (valid) is stored or storage fails. */
export function readStoredIntradayFund(storage?: StorageLike | null): IntradayFundCode {
  try {
    const st = storage === undefined ? defaultStorage() : storage;
    if (!st) return DEFAULT_INTRADAY_FUND;
    return normalizeIntradayFund(st.getItem(INTRADAY_FUND_STORAGE_KEY)) ?? DEFAULT_INTRADAY_FUND;
  } catch {
    return DEFAULT_INTRADAY_FUND;
  }
}

/** Remember the choice; returns false (and never throws) when storage is unavailable. */
export function storeIntradayFund(code: IntradayFundCode, storage?: StorageLike | null): boolean {
  try {
    const st = storage === undefined ? defaultStorage() : storage;
    if (!st) return false;
    st.setItem(INTRADAY_FUND_STORAGE_KEY, code);
    return true;
  } catch {
    return false;
  }
}

export type PnlSource = "ibkr_pnl" | "nav_change_estimate" | "not_funded" | "unavailable";

/** The Day P&L source of a risk/intraday_pnl payload. An older backend (no `source` enum) is read as
 *  IBKR's own P&L when it carries a number, else unavailable. */
export function pnlSourceOf(payload: any): PnlSource | null {
  if (!payload || typeof payload !== "object") return null;
  const s = payload.source;
  if (s === "ibkr_pnl" || s === "nav_change_estimate" || s === "not_funded" || s === "unavailable") return s;
  return typeof payload.day_pnl === "number" ? "ibkr_pnl" : "unavailable";
}

/** True only when the payload is the selected fund's. A payload with no fund block is the legacy
 *  fund-of-record answer, i.e. Horizon's; it is never accepted as Meridian's. */
export function payloadBelongsTo(payload: any, fund: IntradayFundCode): boolean {
  if (!payload || typeof payload !== "object") return false;
  const code = normalizeIntradayFund(payload?.fund?.code);
  if (code == null) return fund === "HORIZON" && payload.fund == null;
  return code === fund;
}

export const ESTIMATE_LABEL =
  "estimated from NAV change since the last close (includes any same-day deposit or withdrawal)";

/** The Day P&L caveat to show under the figure, or null for IBKR's own per-account P&L. */
export function dayPnlSourceLabel(payload: any): string | null {
  const src = pnlSourceOf(payload);
  if (src === "nav_change_estimate") return ESTIMATE_LABEL;
  if (src === "not_funded") return "not funded · no official close to measure a Day P&L from";
  return null;
}

/** "Partial book" warnings only make sense for IBKR's own P&L; an estimate is a whole-account figure. */
export function pnlIsPartial(payload: any, partialFlags: string[]): boolean {
  if (pnlSourceOf(payload) !== "ibkr_pnl") return false;
  if (typeof payload?.day_pnl !== "number") return false;
  const errs: string[] = Array.isArray(payload?.errors) ? payload.errors : [];
  return errs.some((e) => partialFlags.includes(e));
}

/** Sub-line under "Open unrealized P&L": whose account, which IBKR read, or why there is none. */
export function unrealizedSubLabel(payload: any, fundName: string): string {
  if (!payload || typeof payload !== "object") return "—";
  if (payload.fund == null && payload.unrealized_source === undefined) {
    // Legacy payload (older backend): keep the old wording.
    const n = typeof payload.account_count === "number" ? payload.account_count : null;
    return n == null ? "across the fund" : `across ${n} fund sub-account${n === 1 ? "" : "s"}`;
  }
  if (pnlSourceOf(payload) === "not_funded") return "not funded · no positions";
  if (typeof payload.unrealized_pnl !== "number") return `${fundName} · unavailable from this source`;
  if (payload.unrealized_source === "ibkr_ledger") return `${fundName}'s account · IBKR ledger`;
  return `${fundName}'s account · IBKR P&L`;
}

export type Tone = "slate" | "amber" | "green" | "red" | "info";

/**
 * "as of HH:MM" for a NAV-change estimate (review F2). The estimate's live value carries IBKR's own
 * timestamp; when the backend flags it stale (older than 10 minutes in regular hours) the note says
 * "may be stale" and is shown as a warning. Null for any other source.
 */
export function estimateAsOfNote(payload: any): { text: string; stale: boolean } | null {
  if (pnlSourceOf(payload) !== "nav_change_estimate") return null;
  const stale = payload?.net_liq_stale === true;
  const iso = payload?.broker_net_liq_as_of_utc;
  const d = typeof iso === "string" && iso ? new Date(iso) : null;
  if (!d || isNaN(d.getTime())) {
    return { text: stale ? "IBKR value may be stale · no as-of time reported" : "IBKR reported no as-of time for this value", stale };
  }
  const hhmm = d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });
  return { text: stale ? `as of ${hhmm}, may be stale` : `as of ${hhmm}`, stale };
}

/**
 * A HELD IBKR figure (CIO 2026-10-29). While the Trade Desk blotter has the other fund selected in
 * IBKR (~2 s per fund), a P&L read that lands in that window would answer for the wrong fund; the
 * backend instead returns IBKR's LAST Horizon figure (under 2 minutes old, same session), still
 * source "ibkr_pnl", with `held: { as_of_utc, age_s, reason }`. Null unless `held` is well-formed AND
 * the figure is IBKR's own P&L: an older backend (no `held` key), null, or anything malformed is
 * read as not held, and a hold on any other source is ignored.
 */
export interface HeldInfo {
  /** Age of the held figure when the backend answered, whole seconds. */
  ageS: number;
  /** When IBKR produced the held figure (ISO, UTC). */
  asOfUtc: string;
  reason: string;
}

export function heldInfoOf(payload: any): HeldInfo | null {
  if (pnlSourceOf(payload) !== "ibkr_pnl") return null;
  const h = payload?.held;
  if (!h || typeof h !== "object" || Array.isArray(h)) return null;
  const age = h.age_s;
  if (typeof age !== "number" || !Number.isFinite(age) || age < 0) return null;
  const iso = h.as_of_utc;
  if (typeof iso !== "string" || !iso || isNaN(new Date(iso).getTime())) return null;
  return { ageS: Math.floor(age), asOfUtc: iso, reason: typeof h.reason === "string" ? h.reason : "" };
}

/** "Held · IBKR figure 45 s old". `elapsedS` = seconds since the payload arrived, so the age keeps
 *  counting while the held figure stays on screen between reads. */
export function heldPillText(info: HeldInfo, elapsedS = 0): string {
  const s = info.ageS + Math.max(0, Math.floor(elapsedS));
  const age = s < 120 ? `${s} s` : `${Math.floor(s / 60)} min`;
  return `Held · IBKR figure ${age} old`;
}

/** Tooltip for the held pill; the figure's own time in MT (HH:MM:SS). `fundLabel` is the fund of
 *  record's name from the payload's fund block; without one it says "the fund of record". */
export function heldTooltip(info: HeldInfo, fundLabel?: string | null): string {
  const t = new Date(info.asOfUtc).toLocaleTimeString("en-US", {
    timeZone: "America/Denver", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
  });
  const fund = fundLabel && fundLabel.trim() ? fundLabel.trim() : null;
  const why =
    info.reason === "other_fund_selected"
      ? "IBKR's P&L was briefly answering for the other fund (the Trade Desk reads each fund in turn)"
      : `IBKR's P&L was briefly not answering for ${fund ?? "the fund of record"}`;
  const last = fund ? `IBKR's last ${fund} figure` : "IBKR's last figure for the fund of record";
  return `${why}; showing ${last} from ${t} MT.`;
}

/** Header pill describing the Day P&L source (null when IBKR's own P&L is flowing normally). */
export function sourcePill(payload: any): { label: string; tone: Tone } | null {
  const errs: string[] = Array.isArray(payload?.errors) ? payload.errors : [];
  if (errs.includes("fund_of_record_unset")) return { label: "Fund of record not set · no P&L shown", tone: "amber" };
  const src = pnlSourceOf(payload);
  if (src === "nav_change_estimate") return { label: "Day P&L estimated · IBKR not splitting P&L by account", tone: "amber" };
  if (src === "not_funded") return { label: "Not funded", tone: "slate" };
  return null;
}

export interface FundTiles {
  dayPnl: number | null;
  dayPnlText: string;          // the big figure
  dayPnlSub: string;           // the line under it
  valueTitle: string;
  value: number | null;
  valueSub: string;
  unrealized: number | null;
  unrealizedSub: string;
  pill: { label: string; tone: Tone } | null;
  dayPnlPct: number | null;
  /** The estimate's "as of HH:MM[, may be stale]" note (review F2); absent for other sources. */
  asOf?: { text: string; stale: boolean } | null;
}

/**
 * Tiles for a fund that is NOT the fund of record (Meridian). Figures come only from that fund's
 * own payloads; its percentage is measured against its own official close, never Horizon's NAV.
 * `navPayload` is risk/nav_latest?fund=... (its own IBKR account row only), used when the P&L
 * payload carries no live value.
 */
export function otherFundTiles(pnl: any, navPayload: any, fundName: string, error: string | null): FundTiles {
  const src = pnlSourceOf(pnl);
  const navVal = typeof navPayload?.nav?.TotalNAV === "number" ? navPayload.nav.TotalNAV : null;
  const liveVal = typeof pnl?.broker_net_liq === "number" ? pnl.broker_net_liq : navVal;
  const dayPnl = src === "ibkr_pnl" || src === "nav_change_estimate" ? (typeof pnl?.day_pnl === "number" ? pnl.day_pnl : null) : null;
  const priorNav = typeof pnl?.prior_close?.nav === "number" ? pnl.prior_close.nav : null;
  const dayPnlPct = dayPnl != null && priorNav != null && priorNav > 0 ? (dayPnl / priorNav) * 100 : null;

  if (pnl == null) {
    return {
      dayPnl: null, dayPnlText: "—", dayPnlSub: error ? `${fundName} unavailable · ${error}` : `loading ${fundName}…`,
      valueTitle: "Live IBKR value", value: navVal, valueSub: navVal != null ? `${fundName}'s own IBKR account` : "—",
      unrealized: null, unrealizedSub: "—", pill: error ? { label: `${fundName} unavailable`, tone: "amber" } : null, dayPnlPct: null,
    };
  }
  if (src === "not_funded") {
    const hasValue = (pnl.errors || []).includes?.("unfunded_account_has_value");
    return {
      dayPnl: null,
      dayPnlText: "Not funded",
      dayPnlSub: "no official close yet · no Day P&L",
      valueTitle: "Live IBKR value",
      value: liveVal,
      valueSub: liveVal == null
        ? "IBKR account value unavailable"
        : hasValue
        ? "Not funded · the account holds value — check before relying on it"
        : "Not funded · display only, nothing recorded",
      unrealized: null,
      unrealizedSub: "not funded · no positions",
      pill: sourcePill(pnl),
      dayPnlPct: null,
    };
  }
  const label = dayPnlSourceLabel(pnl);
  return {
    dayPnl,
    dayPnlText: dayPnl == null ? "—" : "",
    dayPnlSub: dayPnl == null
      ? "live P&L unavailable"
      : `${dayPnlPct != null ? `${dayPnlPct >= 0 ? "+" : "−"}${Math.abs(dayPnlPct).toFixed(2)}% since prior close` : "since prior close"}${label ? ` · ${label}` : ""}`,
    valueTitle: "Live IBKR value",
    value: liveVal,
    valueSub: `${fundName}'s own IBKR account · not an official NAV`,
    unrealized: typeof pnl?.unrealized_pnl === "number" ? pnl.unrealized_pnl : null,
    unrealizedSub: unrealizedSubLabel(pnl, fundName),
    pill: sourcePill(pnl) ?? (dayPnl == null ? { label: "Live P&L unavailable", tone: "amber" } : null),
    dayPnlPct,
    asOf: dayPnl != null ? estimateAsOfNote(pnl) : null,
  };
}
